/**
 * Build Ionic color CSS variables (base, rgb, contrast, shade, tint).
 * @see https://ionicframework.com/docs/theming/colors
 */

export interface IonicColorSet {
  readonly base: string;
  readonly rgb: string;
  readonly contrast: string;
  readonly contrastRgb: string;
  readonly shade: string;
  readonly tint: string;
}

const IONIC_COLOR_NAMES = [
  'primary',
  'secondary',
  'tertiary',
  'success',
  'warning',
  'danger',
  'light',
  'medium',
  'dark',
] as const;

export type IonicThemeColorName = (typeof IONIC_COLOR_NAMES)[number];

/** All `--ion-color-*` custom properties for one named Ionic color. */
export function buildIonicColorCssVars(
  name: IonicThemeColorName,
  baseHex: string,
): Record<string, string> {
  const set = buildIonicColorSet(baseHex);
  return {
    [`--ion-color-${name}`]: set.base,
    [`--ion-color-${name}-rgb`]: set.rgb,
    [`--ion-color-${name}-contrast`]: set.contrast,
    [`--ion-color-${name}-contrast-rgb`]: set.contrastRgb,
    [`--ion-color-${name}-shade`]: set.shade,
    [`--ion-color-${name}-tint`]: set.tint,
  };
}

/** Apply every property in `vars` onto `root` (typically `document.documentElement`). */
export function applyCssVars(
  root: HTMLElement,
  vars: Record<string, string>,
): void {
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value);
  }
}

export function buildIonicColorSet(hex: string): IonicColorSet {
  const base = normalizeHex(hex) ?? '#3880ff';
  const contrast = pickContrastColor(base);
  return {
    base,
    rgb: hexToRgbTriplet(base),
    contrast,
    contrastRgb: hexToRgbTriplet(contrast),
    shade: mixHex(base, '#000000', 0.12),
    tint: mixHex(base, '#ffffff', 0.12),
  };
}

function pickContrastColor(bgHex: string): string {
  const dark = '#000000';
  const light = '#ffffff';
  return wcagContrastRatio(bgHex, light) >= wcagContrastRatio(bgHex, dark)
    ? light
    : dark;
}

function mixHex(hex: string, mixWith: string, weight: number): string {
  const a = hexToRgb(hex);
  const b = hexToRgb(mixWith);
  if (!a || !b) {
    return hex;
  }
  const w = Math.min(1, Math.max(0, weight));
  const r = Math.round(a.r * (1 - w) + b.r * w);
  const g = Math.round(a.g * (1 - w) + b.g * w);
  const bCh = Math.round(a.b * (1 - w) + b.b * w);
  return rgbToHex(r, g, bCh);
}

function hexToRgbTriplet(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return '0,0,0';
  }
  return `${rgb.r},${rgb.g},${rgb.b}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const n = parseHexNumber(hex);
  if (n === null) {
    return null;
  }
  return {
    r: (n >> 16) & 0xff,
    g: (n >> 8) & 0xff,
    b: n & 0xff,
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((c) => Math.min(255, Math.max(0, c)).toString(16).padStart(2, '0'))
    .join('')}`;
}

function normalizeHex(s: string): string | null {
  const raw = s.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) {
    return null;
  }
  return `#${raw.toLowerCase()}`;
}

function parseHexNumber(s: string): number | null {
  const raw = s.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) {
    return null;
  }
  return Number.parseInt(raw, 16);
}

function wcagContrastRatio(bgHex: string, fgHex: string): number {
  const L1 = relativeLuminance(bgHex);
  const L2 = relativeLuminance(fgHex);
  if (L1 === null || L2 === null) {
    return 1;
  }
  const hi = Math.max(L1, L2);
  const lo = Math.min(L1, L2);
  return (hi + 0.05) / (lo + 0.05);
}

function relativeLuminance(hex: string): number | null {
  const n = parseHexNumber(hex);
  if (n === null) {
    return null;
  }
  const rs = linearize((n >> 16) & 0xff);
  const gs = linearize((n >> 8) & 0xff);
  const bs = linearize(n & 0xff);
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function linearize(chan8: number): number {
  const c = chan8 / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Near-black / near-white page text (same rule as legacy page background service). */
export function pageContrastTextColor(bg: string): string {
  if (normalizeHex(bg) === null) {
    return '#121212';
  }
  const dark = '#121212';
  const light = '#f4f4f4';
  return wcagContrastRatio(bg, light) >= wcagContrastRatio(bg, dark)
    ? light
    : dark;
}

/** Matches ion-content ``--ion-card-background`` (78% page / 22% contrast). */
export function pageCardBackgroundColor(
  pageBg: string,
  pageContrast: string,
): string {
  const page = normalizeHex(pageBg);
  const contrast = normalizeHex(pageContrast);
  if (!page || !contrast) {
    return normalizeHex(pageBg) ?? '#ffffff';
  }
  return mixHex(page, contrast, 0.22);
}

/** Readable text on card / form-control surfaces for the active page theme. */
export function formControlTextColor(
  pageBg: string,
  pageContrast: string,
): string {
  return pageContrastTextColor(pageCardBackgroundColor(pageBg, pageContrast));
}

/**
 * Tint mixed into a base surface (CSS ``color-mix`` parity).
 * ``tintPercent`` is the tint share, e.g. 20 for 20% tint + 80% base.
 */
export function tintedSurfaceColor(
  tintHex: string,
  tintPercent: number,
  baseSurfaceHex: string,
): string {
  const tint = normalizeHex(tintHex);
  const base = normalizeHex(baseSurfaceHex);
  if (!tint || !base) {
    return normalizeHex(baseSurfaceHex) ?? baseSurfaceHex;
  }
  return mixHex(tint, base, (100 - tintPercent) / 100);
}

/** Readable text on a tinted surface for the active page theme. */
export function tintedSurfaceTextColor(
  tintHex: string,
  tintPercent: number,
  baseSurfaceHex: string,
): string {
  return pageContrastTextColor(
    tintedSurfaceColor(tintHex, tintPercent, baseSurfaceHex),
  );
}

export function commaRgbFromHex(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return '18,18,18';
  }
  return `${rgb.r},${rgb.g},${rgb.b}`;
}

/**
 * Primary-tinted action text on raised overlay surfaces (e.g. ion-alert confirm).
 * Uses theme primary when it clears WCAG AA on the overlay; otherwise darkens it.
 */
export function overlayActionTextColor(
  primaryHex: string,
  overlayBgHex: string,
  fallbackHex = '#0d3d6e',
): string {
  const primary = normalizeHex(primaryHex);
  const overlayBg = normalizeHex(overlayBgHex);
  if (!primary || !overlayBg) {
    return fallbackHex;
  }
  if (wcagContrastRatio(overlayBg, primary) >= 4.5) {
    return primary;
  }
  for (const weight of [0.2, 0.35, 0.5, 0.65, 0.8]) {
    const candidate = mixHex(primary, '#000000', weight);
    if (wcagContrastRatio(overlayBg, candidate) >= 4.5) {
      return candidate;
    }
  }
  const fallback = normalizeHex(fallbackHex) ?? '#0d3d6e';
  return wcagContrastRatio(overlayBg, fallback) >= 4.5
    ? fallback
    : pageContrastTextColor(overlayBg);
}

/** Danger/error copy that clears WCAG AA on the page background. */
export function pageErrorTextColor(bg: string): string {
  const candidates = ['#c5000f', '#7a0010', '#fecaca'];
  let best: string | null = null;
  let bestRatio = 0;
  for (const c of candidates) {
    const ratio = wcagContrastRatio(bg, c);
    if (ratio >= 4.5) {
      return c;
    }
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = c;
    }
  }
  const fallback = pageContrastTextColor(bg);
  return wcagContrastRatio(bg, fallback) >= bestRatio
    ? fallback
    : (best ?? fallback);
}

/** Ionic ``--ion-color-step-*`` scale (matches core theme steps). */
export const IONIC_SURFACE_COLOR_STEPS = [
  50, 100, 150, 200, 250, 300, 350, 400, 450, 500, 550, 600, 650, 700, 750,
  800, 850, 900, 950,
] as const;

/**
 * Rebuild Ionic foreground/background step tokens for a surface pair.
 * Alert message copy uses ``--ion-color-step-550`` (muted text), which must
 * derive from the overlay canvas — not the page ``:root`` steps.
 */
export function buildOverlaySurfaceStepCssVars(
  backgroundHex: string,
  textHex: string,
  prefix = '--app-overlay',
): Record<string, string> {
  const bg = normalizeHex(backgroundHex);
  const text = normalizeHex(textHex);
  if (!bg || !text) {
    return {};
  }
  const vars: Record<string, string> = {};
  for (const step of IONIC_SURFACE_COLOR_STEPS) {
    const weight = step / 1000;
    vars[`${prefix}-color-step-${step}`] = mixHex(text, bg, weight);
    vars[`${prefix}-text-color-step-${step}`] = mixHex(bg, text, weight);
  }
  return vars;
}
